import {
  newTracker as _newTracker,
  BrowserTracker,
  TrackerConfiguration,
} from '@snowplow/browser-tracker';
import { getConfig } from './config';

export type FacadeTrackerOptions = Omit<TrackerConfiguration, 'appId' | 'postPath'> & {
  appId?: string;
};

const REJECTED_OPTIONS = ['postPath', 'endpoint'] as const;

const hasOwn = (obj: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key);

export function newTracker(
  namespace: string,
  options: FacadeTrackerOptions = {},
): BrowserTracker | undefined {
  const cfg = getConfig();

  if (options === null || typeof options !== 'object') {
    throw new Error('gokwik.snowplow.newTracker: options must be an object');
  }

  for (const key of REJECTED_OPTIONS) {
    if (hasOwn(options, key)) {
      throw new Error(
        `gokwik.snowplow.newTracker: "${key}" is not configurable. ` +
          `The canonical Snowplow path /com.snowplowanalytics.snowplow/tp2 and the ` +
          `collector host from gokwik.init({ host }) are non-negotiable.`,
      );
    }
  }

  if (hasOwn(options, 'appId')) {
    const callerAppId = (options as { appId?: unknown }).appId;
    if (callerAppId !== undefined && callerAppId !== cfg.merchantId) {
      throw new Error(
        `gokwik.snowplow.newTracker: appId="${String(callerAppId)}" must equal ` +
          `the merchantId="${cfg.merchantId}" supplied to gokwik.init({ merchantId }).`,
      );
    }
  }

  const finalOptions: TrackerConfiguration = {
    ...(options as TrackerConfiguration),
    appId: cfg.merchantId,
    // The gokwik collector serves wildcard ("*") CORS; browsers forbid
    // credentialed requests against a "*" response. Snowplow defaults
    // withCredentials to true, so force it off — non-negotiable like postPath.
    withCredentials: false,
  };

  const tracker = _newTracker(namespace, cfg.host, finalOptions);
  if (tracker) {
    lockSetCollectorUrlOnInstance(tracker);
  }
  return tracker;
}

function lockSetCollectorUrlOnInstance(tracker: BrowserTracker): void {
  try {
    Object.defineProperty(tracker, 'setCollectorUrl', {
      value: () => {
        throw new Error(
          'gokwik.snowplow.newTracker(...).setCollectorUrl: collector URL is locked ' +
            'to the host supplied to gokwik.init({ host }); re-pointing live trackers ' +
            'is not allowed.',
        );
      },
      writable: false,
      configurable: false,
      enumerable: true,
    });
  } catch {
    /* tracker object frozen or otherwise non-writable — fail closed downstream */
  }
}

/**
 * Locked: collector URL is fixed at gokwik.init({ host }) time per plan §5.1.
 * Snowplow's setCollectorUrl would silently repoint a live tracker; we
 * shadow it here to forbid the bypass. Local export wins over `export *`.
 */
export function setCollectorUrl(_url: string): void {
  throw new Error(
    'gokwik.snowplow.setCollectorUrl: collector URL is locked to the host ' +
      'supplied to gokwik.init({ host }); re-pointing live trackers is not allowed.',
  );
}

export * from '@snowplow/browser-tracker';
